<template>
  <layout-page
    :anchors="[{ path: 'terms_of_service', label: $t('links.page_privacy_policy', 'Terms of Service') }]"
    :activeSection="'terms_of_service'"
  >
    <section ref="heroSection" class="page-hero">
      <p class="page-hero__eyebrow">{{ $t('privacy.eyebrow', 'Legal') }}</p>
      <h1 class="page-hero__title">{{ $t('privacy.title', 'Terms of Service') }}</h1>
      <p class="page-hero__date">{{ $t('privacy.effective_date', 'Effective Date: May 2026') }}</p>
    </section>

    <div class="layout">
<article>
  <p class="article__intro">
    {{ $t('tos.intro_line1', 'Welcome to ParcelPoint. These Terms of Service ("Terms") govern access to and use of ParcelPoint.us, related platform features, operational tools, logistics workflows, and services made available through or in connection with ParcelPoint.') }}
    <br><br>
    {{ $t('tos.intro_line2', 'ParcelPoint is an affiliated logistics technology platform used in connection with StockFore Inc. operations to support shipment intake, warehouse workflows, package processing, tracking coordination, customer communication, and logistics-related operational infrastructure.') }}
    <br><br>
    {{ $t('tos.intro_line3', 'By accessing or using ParcelPoint, you agree to these Terms.') }}
  </p>

  <div class="article__section" id="s1">
    <h2>{{ $t('tos.s1_title', 'Platform Purpose') }}</h2>
    <p>{{ $t('tos.s1_text1', 'ParcelPoint is designed to support logistics-related workflows, including package intake, warehouse processing, shipment coordination, parcel tracking, customer communication, and operational logistics management.') }}</p>
    <p>{{ $t('tos.s1_text2', 'ParcelPoint may be used in connection with StockFore Inc. logistics, warehousing, freight forwarding, procurement coordination, and supply chain support activities.') }}</p>
  </div>
  <hr class="article__divider" />

  <div class="article__section" id="s2">
    <h2>{{ $t('tos.s2_title', 'User Responsibilities') }}</h2>
    <p>{{ $t('tos.s2_text1', 'Users agree to provide accurate, complete, and lawful information when using the platform or submitting shipment-related data.') }}</p>
    <p>{{ $t('tos.s2_text2', 'Users are responsible for ensuring that shipment contents, product descriptions, customs declarations, values, recipient information, and destination details are accurate and comply with applicable laws, carrier rules, export controls, import requirements, and destination-country regulations.') }}</p>
  </div>
  <hr class="article__divider" />

  <div class="article__section" id="s3">
    <h2>{{ $t('tos.s3_title', 'Prohibited Use') }}</h2>
    <p>{{ $t('tos.s3_text1', 'ParcelPoint may not be used for illegal, fraudulent, restricted, or prohibited activities. Prohibited items may include, but are not limited to:') }}</p>
    <ul>
      <li>{{ $t('tos.s3_item1', 'Illegal substances;') }}</li>
      <li>{{ $t('tos.s3_item2', 'Counterfeit goods;') }}</li>
      <li>{{ $t('tos.s3_item3', 'Stolen merchandise;') }}</li>
      <li>{{ $t('tos.s3_item4', 'Weapons or restricted military items;') }}</li>
      <li>{{ $t('tos.s3_item5', 'Hazardous materials prohibited by law or carriers;') }}</li>
      <li>{{ $t('tos.s3_item6', 'Items restricted by export, import, customs, or sanctions regulations;') }}</li>
      <li>{{ $t('tos.s3_item7', 'Any shipment or transaction involving fraud, misrepresentation, or unlawful activity.') }}</li>
    </ul>
    <p>{{ $t('tos.s3_text2', 'ParcelPoint and StockFore Inc. reserve the right to refuse, suspend, inspect, hold, or cancel processing of any shipment or account when required for operational, legal, compliance, or security reasons.') }}</p>
  </div>
  <hr class="article__divider" />

  <div class="article__section" id="s4">
    <h2>{{ $t('tos.s4_title', 'Third-Party Services') }}</h2>
    <p>{{ $t('tos.s4_text1', 'ParcelPoint may interact with third-party carriers, warehouse providers, logistics partners, payment processors, customs brokers, software vendors, and other service providers.') }}</p>
    <p>{{ $t('tos.s4_text2', 'ParcelPoint does not control third-party carrier schedules, customs procedures, government inspections, delivery conditions, or external service provider performance.') }}</p>
  </div>
  <hr class="article__divider" />

  <div class="article__section" id="s5">
    <h2>{{ $t('tos.s5_title', 'Shipping, Tracking, and Delivery') }}</h2>
    <p>{{ $t('tos.s5_text1', 'Tracking information, shipment status updates, delivery estimates, and logistics data may be provided for convenience and operational visibility.') }}</p>
    <p>{{ $t('tos.s5_text2', 'Delivery times are estimates only and are not guaranteed unless expressly agreed in writing by StockFore Inc.') }}</p>
    <p>{{ $t('tos.s5_text3', 'Delays may occur due to carrier operations, customs inspection, weather, destination-country procedures, regulatory requirements, or other circumstances outside platform control.') }}</p>
  </div>
  <hr class="article__divider" />

  <div class="article__section" id="s6">
    <h2>{{ $t('tos.s6_title', 'Account Access and Security') }}</h2>
    <p>{{ $t('tos.s6_text1', 'Users are responsible for maintaining the confidentiality of login credentials and account access.') }}</p>
    <p>{{ $t('tos.s6_text2', 'Users must notify the platform operator promptly of any unauthorized access, suspicious activity, or security concern.') }}</p>
    <p>{{ $t('tos.s6_text3', 'ParcelPoint may suspend or restrict access to accounts or platform features for security, compliance, operational, or misuse concerns.') }}</p>
  </div>
  <hr class="article__divider" />

  <div class="article__section" id="s7">
    <h2>{{ $t('tos.s7_title', 'Data and Platform Use') }}</h2>
    <p>{{ $t('tos.s7_text1', 'Information submitted through ParcelPoint may be used to support logistics operations, warehouse workflows, shipment tracking, customer coordination, reporting, and related operational processes.') }}</p>
    <p>{{ $t('tos.s7_text2', 'Use of ParcelPoint is also governed by the <a href="/en/privacy-policy/">ParcelPoint Privacy Policy</a>.') }}</p>
  </div>
  <hr class="article__divider" />

  <div class="article__section" id="s8">
    <h2>{{ $t('tos.s8_title', 'Intellectual Property') }}</h2>
    <p>{{ $t('tos.s8_text1', 'All software interfaces, platform workflows, website content, graphics, systems, data structures, and operational tools are owned by their respective owners or licensed users.') }}</p>
    <p>{{ $t('tos.s8_text2', 'No content, software, design, or operational workflow may be copied, reproduced, reverse engineered, distributed, or commercially used without prior written authorization.') }}</p>
  </div>
  <hr class="article__divider" />

  <div class="article__section" id="s9">
    <h2>{{ $t('tos.s9_title', 'Limitation of Liability') }}</h2>
    <p>{{ $t('tos.s9_text1', 'To the maximum extent permitted by law, ParcelPoint and StockFore Inc. shall not be liable for indirect, incidental, consequential, special, punitive, or exemplary damages, including loss of profits, business interruption, loss of data, shipment delays, customs actions, carrier failures, or third-party service issues.') }}</p>
    <p>{{ $t('tos.s9_text2', 'The platform is provided on an "as is" and "as available" basis.') }}</p>
  </div>
  <hr class="article__divider" />

  <div class="article__section" id="s10">
    <h2>{{ $t('tos.s10_title', 'Service Changes') }}</h2>
    <p>{{ $t('tos.s10_text1', 'ParcelPoint may modify, update, suspend, or discontinue any platform feature, workflow, page, service, or integration at any time without prior notice.') }}</p>
  </div>
  <hr class="article__divider" />

  <div class="article__section" id="s11">
    <h2>{{ $t('tos.s11_title', 'Governing Law') }}</h2>
    <p>{{ $t('tos.s11_text1', 'These Terms shall be governed by the laws of the State of Delaware, United States, without regard to conflict of law principles.') }}</p>
  </div>
  <hr class="article__divider" />

  <div class="article__section" id="s12">
    <h2>{{ $t('tos.s12_title', 'Contact Information') }}</h2>
    <div class="contact-card">
      <p><strong>{{ $t('tos.houston_office', 'Houston Operations Office') }}</strong></p>
      <p>5373 West Alabama St, Ste 590</p>
      <p>Houston, Texas 77056, United States</p>
      <p style="margin-top:14px;">
        {{ $t('tos.email_label', 'Email:') }}
        <a href="mailto:ceo@parcelpoint.us">ceo@parcelpoint.us</a>
      </p>
      <p>
        {{ $t('tos.phone_label', 'Phone:') }}
        <a href="tel:+15127853401">+1 (512) 785-3401</a>
      </p>
      <p class="contact-card__note">
        {{ $t('tos.note', 'ParcelPoint is an affiliated logistics technology platform used in connection with StockFore Inc. operations.') }}
      </p>
    </div>
  </div>
</article>

      <aside ref="tocWrapper" class="toc-wrapper">
        <nav ref="toc" class="toc" :class="{ 'toc--visible': isVisible, 'toc--hidden': !isVisible }" :style="tocStyle">
          <p class="toc__title">{{ $t('privacy.toc_title', 'Contents') }}</p>
          <ol class="toc__list">
            <li :class="{ active: activeSection === 's1' }">
              <a href="#" @click.prevent="scrollToSection('s1')">1. {{ $t('tos.s1_title', 'Platform Purpose') }}</a>
            </li>
            <li :class="{ active: activeSection === 's2' }">
              <a href="#" @click.prevent="scrollToSection('s2')">2. {{ $t('tos.s2_title', 'User Responsibilities') }}</a>
            </li>
            <li :class="{ active: activeSection === 's3' }">
              <a href="#" @click.prevent="scrollToSection('s3')">3. {{ $t('tos.s3_title', 'Prohibited Use') }}</a>
            </li>
            <li :class="{ active: activeSection === 's4' }">
              <a href="#" @click.prevent="scrollToSection('s4')">4. {{ $t('tos.s4_title', 'Third-Party Services') }}</a>
            </li>
            <li :class="{ active: activeSection === 's5' }">
              <a href="#" @click.prevent="scrollToSection('s5')">5. {{ $t('tos.s5_title', 'Shipping, Tracking, and Delivery') }}</a>
            </li>
            <li :class="{ active: activeSection === 's6' }">
              <a href="#" @click.prevent="scrollToSection('s6')">6. {{ $t('tos.s6_title', 'Account Access and Security') }}</a>
            </li>
            <li :class="{ active: activeSection === 's7' }">
              <a href="#" @click.prevent="scrollToSection('s7')">7. {{ $t('tos.s7_title', 'Data and Platform Use') }}</a>
            </li>
            <li :class="{ active: activeSection === 's8' }">
              <a href="#" @click.prevent="scrollToSection('s8')">8. {{ $t('tos.s8_title', 'Intellectual Property') }}</a>
            </li>
            <li :class="{ active: activeSection === 's9' }">
              <a href="#" @click.prevent="scrollToSection('s9')">9. {{ $t('tos.s9_title', 'Limitation of Liability') }}</a>
            </li>
            <li :class="{ active: activeSection === 's10' }">
              <a href="#" @click.prevent="scrollToSection('s10')">10. {{ $t('tos.s10_title', 'Service Changes') }}</a>
            </li>
            <li :class="{ active: activeSection === 's11' }">
              <a href="#" @click.prevent="scrollToSection('s11')">11. {{ $t('tos.s11_title', 'Governing Law') }}</a>
            </li>
            <li :class="{ active: activeSection === 's12' }">
              <a href="#" @click.prevent="scrollToSection('s12')">12. {{ $t('tos.s12_title', 'Contact Information') }}</a>
            </li>
          </ol>
        </nav>
      </aside>
    </div>
  </layout-page>
</template>

<script>
import LayoutPage from '@/components/LayoutPage.vue';

export default {
  name: 'PrivacyPolicy',
  components: { LayoutPage },
  data() {
    return {
      activeSection: 's1',
      isVisible: false,
      tocStyle: {
        position: 'fixed',
        top: '80px',
        right: '100px',     // ← здесь задаёте отступ от правого края
        width: '220px',
      },
      sectionIds: ['s1', 's2', 's3', 's4', 's5', 's6', 's7', 's8', 's9'],
      observer: null,
      footerElement: null,
      originalTop: 80,
    };
  },
  mounted() {
    document.body.classList.add('privacy-policy-page');
    this.initObserver();
    this.footerElement = document.querySelector('footer');
    this.calculateOriginalPosition();
    window.addEventListener('scroll', this.handleScroll);
    window.addEventListener('resize', this.handleResize);
    this.handleScroll();
  },
  beforeDestroy() {
    document.body.classList.remove('privacy-policy-page');
    if (this.observer) this.observer.disconnect();
    window.removeEventListener('scroll', this.handleScroll);
    window.removeEventListener('resize', this.handleResize);
  },
  methods: {
    scrollToSection(sectionId) {
      const element = document.getElementById(sectionId);
      if (element) {
        const offset = 80;
        const top = element.getBoundingClientRect().top + window.pageYOffset - offset;
        window.scrollTo({ top, behavior: 'smooth' });
        this.activeSection = sectionId;
      }
    },
    initObserver() {
      const options = { rootMargin: '-20% 0px -60% 0px', threshold: 0 };
      this.observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const id = entry.target.id;
            if (id && this.sectionIds.includes(id)) this.activeSection = id;
          }
        });
      }, options);
      this.sectionIds.forEach((id) => {
        const el = document.getElementById(id);
        if (el) this.observer.observe(el);
      });
    },
    calculateOriginalPosition() {
      // Создаём временный элемент, чтобы узнать, где должно быть меню в потоке
      const dummy = document.createElement('div');
      dummy.style.width = '220px';
      dummy.style.height = '1px';
      dummy.style.position = 'absolute';
      dummy.style.right = '40px'; // такой же отступ, как у фиксированного меню
      dummy.style.top = '0';
      document.querySelector('.layout')?.appendChild(dummy);
      const rect = dummy.getBoundingClientRect();
      this.originalTop = rect.top + window.pageYOffset;
      dummy.remove();
    },
    handleScroll() {
      const scrollTop = window.pageYOffset;
      const tocHeight = 400; // примерная высота меню
      const shouldShow = scrollTop + 80 > this.originalTop + tocHeight;

      let willHitFooter = false;
      if (this.footerElement) {
        const footerRect = this.footerElement.getBoundingClientRect();
        const footerTop = footerRect.top + scrollTop;
        const tocBottomWhenFixed = scrollTop + 80 + tocHeight;
        if (tocBottomWhenFixed + 20 > footerTop) {
          willHitFooter = true;
        }
      }

      this.isVisible = shouldShow && !willHitFooter && window.innerWidth > 900;
    },
    handleResize() {
      this.calculateOriginalPosition();
      this.handleScroll();
    },
  },
};
</script>

<style scoped>
.page-hero {
  width: 100vw;
  position: relative;
  left: 50%;
  right: 50%;
  margin-left: -50vw;
  margin-right: -50vw;
  background: #111;
  padding: 72px 40px 60px;
  text-align: center;
}
.page-hero__eyebrow {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: #e63329;
  margin-bottom: 14px;
}
.page-hero__title {
  font-size: 48px;
  font-weight: 800;
  color: #fff;
  letter-spacing: -0.04em;
  margin-bottom: 14px;
  line-height: 1.1;
}
.page-hero__date {
  font-size: 14px;
  color: #666;
}
.layout {
  max-width: 1000px;
  margin: 0 auto;
  padding: 64px 40px 96px;
  display: grid;
  grid-template-columns: 1fr 220px;
  gap: 64px;
  align-items: start;
}
.toc-wrapper {
  position: relative;
  display: flex;
}
.toc {
  background: #f8f8f8;
  border-radius: 12px;
  padding: 22px 20px;
  transition: opacity 0.2s, visibility 0.2s;
  box-shadow: 0 8px 20px rgba(0,0,0,0.1);
  z-index: 100;
  opacity: 0;
  visibility: hidden;
}
.toc--visible {
  opacity: 1;
  visibility: visible;
}
.toc--hidden {
  opacity: 0;
  visibility: hidden;
}
.toc--fixed {
  box-shadow: 0 8px 20px rgba(0,0,0,0.1);
  z-index: 100;
}
.toc__title {
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: #999;
  margin-bottom: 14px;
}
.toc__list {
  list-style: none;
  padding: 0;
}
.toc__list li {
  margin-bottom: 9px;
}
.toc__list a {
  font-size: 13px;
  color: #555;
  text-decoration: none;
  line-height: 1.4;
  font-weight: 500;
  display: inline-block;
  transition: color 0.15s;
}
.toc__list a:hover {
  color: #e63329;
}
.toc__list li.active a {
  color: #e63329;
  font-weight: 700;
}
.article__intro {
  font-size: 15px;
  color: #444;
  background: #fff5f5;
  border-left: 3px solid #e63329;
  padding: 18px 22px;
  border-radius: 0 10px 10px 0;
  margin-bottom: 48px;
  line-height: 1.8;
}
.article__section {
  margin-bottom: 44px;
}
.article__section h2 {
  font-size: 22px;
  font-weight: 700;
  color: #111;
  margin-bottom: 14px;
  letter-spacing: -0.02em;
}
.article__section p {
  font-size: 15px;
  color: #444;
  margin-bottom: 10px;
}
.article__section ul {
  margin: 10px 0;
  padding-left: 0;
  list-style: none;
}
.article__section ul li {
  font-size: 15px;
  color: #444;
  margin-bottom: 8px;
  padding-left: 18px;
  position: relative;
}
.article__section ul li::before {
  content: '';
  position: absolute;
  left: 0;
  top: 10px;
  width: 6px;
  height: 6px;
  background: #e63329;
  border-radius: 50%;
}
.article__divider {
  border: none;
  border-top: 1px solid #f0f0f0;
  margin: 36px 0;
}
.contact-card {
  background: #f8f8f8;
  border-radius: 12px;
  padding: 28px;
  margin-top: 8px;
}
.contact-card p {
  font-size: 14.5px;
  color: #444;
  margin-bottom: 7px;
}
.contact-card strong {
  color: #111;
}
.contact-card a {
  color: #e63329;
}
.contact-card__note {
  font-size: 13px;
  color: #999;
  margin-top: 14px !important;
}
@media (max-width: 900px) {
  .layout {
    grid-template-columns: 1fr;
    gap: 40px;
  }
  .toc-wrapper {
    order: 2;
  }
  .toc {
    position: static !important;
    width: 100% !important;
    display: none; /* на мобильных меню скрываем */
  }
}
@media (max-width: 600px) {
  .page-hero {
    padding: 48px 20px 40px;
  }
  .page-hero__title {
    font-size: 32px;
  }
  .layout {
    padding: 40px 20px 64px;
  }
  .contact-card {
    padding: 20px;
  }
}
</style>